/* eslint-disable react/prop-types */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ImagePlus, Images, Loader2, Plus, X,
} from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import { imageOptions } from "@/lib/carouselLayout";
import {
  MAX_FB_PHOTOS, addPhoto, movePhoto, photoCountLabel, pickablePhotos, removePhoto,
} from "@/lib/fbPhotos";
import CarouselImageDialog from "@/components/CarouselImageDialog";

// Photos for a Facebook PHOTO post (up to 10, in order). The first is the
// big one in Facebook's grid and the one that carries the sponsor ad.
// Photos come from the article (featured image, carousel library, source
// photos) or "New image" (upload / paste / Unsplash / Pexels, stored in
// the article's photo library like a carousel photo). Reorder with the
// arrows (works on iPad) or by dragging (desktop). See lib/fbPhotos.js.

const BTN = "inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] bg-white hover:border-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed";
const srcOf = (url) => (url && !url.startsWith("http") ? `${BACKEND_BASE}${url}` : url);
const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function FacebookPhotoPicker({ article, photos, setPhotos }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const full = photos.length >= MAX_FB_PHOTOS;
  const choices = pickablePhotos(imageOptions(article), photos);

  const setLibrary = (library) => {
    qc.setQueryData(["article", article.id], (old) => (old ? { ...old, carousel_images: library } : old));
    qc.invalidateQueries({ queryKey: ["article", article.id] });
  };

  // A source photo has to be copied into our storage before Facebook can
  // fetch it from us — same import the carousel uses.
  const importSource = useMutation({
    mutationFn: (opt) =>
      api.importCarouselImage(article.id, { kind: "source", url: opt.importFrom, credit: opt.credit }),
    onSuccess: (r, opt) => {
      setLibrary(r.carousel_images);
      setPhotos((cur) => addPhoto(cur, { url: r.image.url, label: opt.label }));
    },
    onError: (e) => toast.error(errText(e, "Could not add that photo")),
  });

  const pick = (opt) => {
    if (full) {
      toast.message(`A Facebook post can carry up to ${MAX_FB_PHOTOS} photos.`);
      return;
    }
    if (opt.importFrom) importSource.mutate(opt);
    else setPhotos((cur) => addPhoto(cur, opt));
  };

  return (
    <div data-testid="fb-share-photos" className="space-y-2">
      <CarouselImageDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        article={article}
        purpose="facebook"
        onAdded={(image, library) => {
          setLibrary(library);
          setPhotos((cur) => addPhoto(cur, { url: image.url, label: "New photo" }));
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Images className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
        <span className="overline">Photos</span>
        <span className="text-[11px] text-[var(--ink-muted)]" data-testid="fb-share-photo-count">
          {photoCountLabel(photos.length)} · up to {MAX_FB_PHOTOS}
        </span>
      </div>

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="fb-share-photo-list">
          {photos.map((p, i) => (
            <div
              key={p.url}
              data-testid={`fb-share-photo-${i}`}
              draggable
              onDragStart={() => setDragFrom(i)}
              onDragEnd={() => setDragFrom(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragFrom !== null) setPhotos((cur) => movePhoto(cur, dragFrom, i));
                setDragFrom(null);
              }}
              className={
                "w-24 border bg-white " +
                (i === 0 ? "border-[#1877F2]" : "border-[var(--border)]") +
                (dragFrom === i ? " opacity-50" : "")
              }
              title={p.label}
            >
              <div className="relative w-full h-20 bg-[var(--secondary)] overflow-hidden cursor-move">
                <img src={srcOf(p.url)} alt={p.label} className="w-full h-full object-cover" draggable={false} />
                <span className="absolute left-0 top-0 px-1 text-[10px] text-white bg-black/60">
                  {i === 0 ? "1 · Main" : i + 1}
                </span>
                <button
                  type="button"
                  aria-label="Remove photo"
                  data-testid={`fb-share-photo-remove-${i}`}
                  onClick={() => setPhotos((cur) => removePhoto(cur, i))}
                  className="absolute right-0 top-0 p-0.5 text-white bg-black/60 hover:bg-black"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="flex">
                <button
                  type="button"
                  aria-label="Move photo left"
                  disabled={i === 0}
                  onClick={() => setPhotos((cur) => movePhoto(cur, i, i - 1))}
                  className="flex-1 flex justify-center py-0.5 border-r border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-30"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Move photo right"
                  disabled={i === photos.length - 1}
                  onClick={() => setPhotos((cur) => movePhoto(cur, i, i + 1))}
                  className="flex-1 flex justify-center py-0.5 hover:bg-[var(--secondary)] disabled:opacity-30"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="fb-share-photo-add"
          className={BTN}
          disabled={full || choices.length === 0}
          onClick={() => setAdding((v) => !v)}
        >
          <Plus className="w-3.5 h-3.5" />
          {adding ? "Done adding" : "Add from this article"}
        </button>
        <button
          type="button"
          data-testid="fb-share-photo-new"
          className={BTN}
          disabled={full}
          onClick={() => setDialogOpen(true)}
        >
          <ImagePlus className="w-3.5 h-3.5" /> New image
        </button>
        {importSource.isPending && (
          <span className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Preparing photo…
          </span>
        )}
      </div>

      {adding && !full && choices.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-64 overflow-y-auto" data-testid="fb-share-photo-choices">
          {choices.map((o) => (
            <button
              key={o.url}
              type="button"
              disabled={importSource.isPending}
              onClick={() => pick(o)}
              title={o.label}
              className="relative h-20 bg-[var(--secondary)] overflow-hidden border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
            >
              <img src={srcOf(o.url)} alt={o.label} loading="lazy" className="w-full h-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[10px] text-white bg-black/50 truncate text-left">
                + {o.label}
              </span>
            </button>
          ))}
        </div>
      )}

      <p className="text-[11px] text-[var(--ink-muted)]">
        The first photo is the big one in the post and the one the sponsor ad goes on. Drag a photo, or use the
        arrows, to change the order.
      </p>
    </div>
  );
}
